'use client';

import React, {
  cloneElement,
  DetailedHTMLProps,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import Link from 'next/link';
import { v4 as uuidv4 } from 'uuid';

import cn from '@/helpers/utils/classNames';

interface RawRadioProps
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'children' | 'type' | 'value' | 'checked' | 'onChange'
  > {
  currentValue?: string;

  children: ReactNode;
  onChange?: (value: string) => void;
  containerClassName?: string;
  onMount?: () => void;
  labelClassName?: string;
  isShoppablePage?: boolean;
}

export type RadioProps = RawRadioProps &
  (
    | {
        as?: 'Link';
        href?: string;
      }
    | {
        as?: 'radio';
        defaultValue: string;
      }
  );

export default forwardRef<HTMLInputElement, RadioProps>(function Radio(props, ref) {
  const {
    children,
    className,
    currentValue,
    defaultValue,
    onMount,
    containerClassName,
    disabled,
    labelClassName,
    onChange,
    as,
    isShoppablePage,
    ...restProps
  } = props;

  const isChecked = defaultValue === currentValue;

  const inputId = uuidv4();

  useEffect(() => {
    if (onMount) onMount();
  }, [onMount]);

  return (
    <div className={cn('v2-flex v2-w-auto v2-items-center', containerClassName, disabled && 'v2-cursor-not-allowed')}>
      <label
        className={cn(
          'v2-font-sweet-sans-pro v2-text-xl v2-group/radio-btn v2-relative v2-h-full v2-cursor-pointer v2-select-none v2-pl-[30px] v2-text-[#212427] sm:!text-[18px] sm:!leading-[30px] md:!text-[21px] md:!leading-[31px]',
          labelClassName,
          disabled && ' v2-pointer-events-none',
          isShoppablePage && 'v2-font-sweet-sans-pro'
        )}
        htmlFor={inputId}
      >
        {as === 'Link' && props.href ? (
          <Link href={props.href} className={cn(`v2-peer/radio-btn v2-radio-btn v2-appearance-none`)}>
            <span
              className={cn(
                'v2-check-mark v2-absolute v2-top-[5px] v2-left-0 v2-h-[22px] v2-w-[22px] v2-rounded-full v2-border v2-border-[#212427] v2-bg-transparent after:v2-absolute after:v2-top-[50%] after:v2-left-[50%] after:-v2-translate-x-1/2 after:-v2-translate-y-1/2 after:v2-z-[1] after:v2-h-[10px] after:v2-w-[10px] after:v2-rounded-full group-hover/radio-btn:v2-border-transparent group-hover/radio-btn:!v2-shadow-[0_0px_0px_4px_rgba(22,70,52,0.24)]',
                {
                  'v2-border-primary !v2-bg-transparent after:v2-bg-primary': isChecked,
                },
                className
              )}
            ></span>
            {children}
          </Link>
        ) : (
          <>
            {children}
            <input
              id={inputId}
              ref={ref}
              className={cn(`v2-peer/radio-btn v2-radio-btn v2-appearance-none`)}
              type="radio"
              value={defaultValue}
              checked={isChecked}
              onChange={() => onChange && onChange(defaultValue as string)}
              disabled={disabled}
              {...restProps}
            />
            <span
              className={cn(
                'v2-check-mark v2-absolute v2-top-[4px] v2-left-0 v2-h-[22px] v2-w-[22px] v2-rounded-full v2-border v2-border-[#212427] v2-bg-transparent after:v2-absolute after:v2-top-[50%] after:v2-left-[50%] after:-v2-translate-x-1/2 after:-v2-translate-y-1/2 after:v2-z-10 after:v2-h-[10px] after:v2-w-[10px] after:v2-rounded-full after:v2-bg-transparent peer-checked/radio-btn:after:v2-bg-primary group-hover/radio-btn:v2-border-transparent group-hover/radio-btn:!v2-shadow-[0_0px_0px_4px_rgba(22,70,52,0.24)] peer-checked/radio-btn:v2-border-primary peer-checked/radio-btn:v2-bg-transparent peer-focus-within/radio-btn:!v2-shadow-[0_0px_0px_4px_rgba(22,70,52,0.24)] peer-focus/radio-btn:!v2-shadow-[0_0px_0px_4px_rgba(22,70,52,0.24)] peer-disabled/radio-btn:!v2-border-gray-300 peer-disabled:peer-checked/radio-btn:!v2-shadow-none',
                className
              )}
            ></span>
          </>
        )}
      </label>
    </div>
  );
});

export type RadioGroupProps = Pick<RadioProps, 'onChange' | 'name'> & {
  initialSelectedValue: string;
  children: React.ReactElement<RadioProps>[] | ReactNode;
  className?: string;
  onMount?: () => void;
};

export const RadioGroup = ({
  children,
  onChange,
  onMount,
  initialSelectedValue: propValue,
  name,
  className,
}: RadioGroupProps) => {
  const [currentValue, setValue] = useState<string>(propValue);

  useEffect(() => {
    setValue(() => propValue);
  }, [propValue]);

  useEffect(() => {
    if (onMount) onMount();
  }, [onMount]);

  return (
    <div key={name} className={cn('', className)}>
      {React.Children.map(children as React.ReactElement<RadioProps>[], (child: React.ReactElement<RadioProps>) =>
        child
          ? cloneElement(child, {
              ...child.props,
              currentValue,
              onChange: (value: string) => {
                setValue(value);
                onChange && onChange(value);
                child.props.onChange && child.props.onChange(value);
              },
            })
          : null
      )}
    </div>
  );
};
